import React, {useState} from "react";
import {KatButton, KatModal} from "@amzn/katal-react";
import CustomDropdown from "src/app/component/common/CustomDropdown";
import {ODQ_OFFBOARD_ROOT_CAUSES, ODQ_RELIABILITY_OFFBOARD_REASON_CODES} from "src/app/config/DropDownConfig";

interface OffboardDecisionModalProps {
    visible: boolean;
    caseSubType: string;
    onSubmit: (reasonCode: string, rootCause: string) => void;
    onClose: () => void;
}

/**
 * OffboardDecisionModal - popup shown when the auditor clicks Offboard.
 * Captures the mandatory reasonCode (both ODQ and Reliability) and, for ODQ only,
 * the mandatory rootCause. Offboard stays disabled until all required values are selected.
 * Reuses the shared CustomDropdown + existing odq-exempt-modal styles (SLPTicketModal pattern).
 */
const OffboardDecisionModal: React.FC<OffboardDecisionModalProps> = ({visible, caseSubType, onSubmit, onClose}) => {
    const [reasonCode, setReasonCode] = useState("");
    const [rootCause, setRootCause] = useState("");
    const isODQ = caseSubType === "ODQ";

    const resetForm = () => {
        setReasonCode("");
        setRootCause("");
    };

    const handleClose = () => {
        onClose();
        resetForm();
    };

    const handleSubmit = () => {
        onSubmit(reasonCode, isODQ ? rootCause : "");
        resetForm();
    };

    // rootCause is required for ODQ only.
    const isValid = !!reasonCode && (!isODQ || !!rootCause);

    return (
        <KatModal visible={visible} onClose={handleClose}>
            <span slot="title">Offboard Decision</span>
            <div className="odq-exempt-modal">
                <div className="odq-exempt-modal__field">
                    <CustomDropdown
                        label="Reason Code"
                        placeholder="--Select Reason Code--"
                        options={ODQ_RELIABILITY_OFFBOARD_REASON_CODES}
                        value={reasonCode}
                        onChange={setReasonCode}
                        testId="offboard-reason-select"
                    />
                </div>

                {isODQ && (
                    <div className="odq-exempt-modal__field">
                        <CustomDropdown
                            label="Root Cause"
                            placeholder="--Select Root Cause--"
                            options={ODQ_OFFBOARD_ROOT_CAUSES}
                            value={rootCause}
                            onChange={setRootCause}
                            testId="offboard-rootcause-select"
                        />
                    </div>
                )}
            </div>
            <div slot="footer" className="kat-group-horizontal">
                <KatButton variant="secondary" label="Cancel" onClick={handleClose}
                           data-testid="offboard-cancel-btn" />
                <KatButton variant="primary" label="Offboard"
                           disabled={!isValid}
                           onClick={handleSubmit}
                           data-testid="offboard-submit-btn" />
            </div>
        </KatModal>
    );
};

export default OffboardDecisionModal;
